import { ThunkResult } from "common/reducers";
import { handleErrors } from "common/reducers/Utils";
import { MeasurementActions } from "common/reducers/measurement.actions";
import { RootDispatchType, RootGetState } from "common/store";
import { load, update } from "common/api/measurement.service";
import { currentMeasurementId } from "common/selectors/measurement.common.selectors";
import { currentMeasurementHydrated } from "common/selectors/measurement.selectors";
import { addFlashAlert, SUCCESS } from "common/components/FlashAlert";
import { waitEnabledMeasurements } from "src/router/loaders/helper";

export const MeasurementAsyncActions = {
  /**
   * Resolves `true` on success, `false` on failure (guard miss or a swallowed service error).
   * Callers that don't need the outcome can ignore it; the save pipeline (`saveAnnotator`) uses it
   * to abort cleanly rather than report a failed persist/re-sync as a successful save.
   */
  loadMeasurement: (include: string[] = []): ThunkResult<Promise<boolean>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState): Promise<boolean> => {
      await waitEnabledMeasurements();
      const measurementId = currentMeasurementId(getState());
      if (!measurementId) {
        addFlashAlert("danger", "No measurement id found");
        return false;
      }

      try {
        dispatch(MeasurementActions.fetchMeasurement(measurementId));

        const response = await load(measurementId, include);

        dispatch(MeasurementActions.receiveMeasurement(response.measurement));
        return true;
      } catch (response) {
        handleErrors(response);
        dispatch(MeasurementActions.setMeasurementLoaded(measurementId));
        return false;
      }
    };
  },
  /**
   * Resolves `true` on success, `false` on failure — see `loadMeasurement`. The service error is
   * still swallowed (flash + setMeasurementLoaded); the boolean lets a caller detect the failure.
   *
   * `silent` suppresses the success flash AND the `setMeasurementEditing(false)` toggle. The
   * Window-inator save pipeline (`saveAnnotator`) uses this for its mid-pipeline measurement persist:
   * a "Measurement updated successfully" flash + editing-mode exit fired there would be premature —
   * the elevations haven't persisted yet, and the wizard owns its own end-of-save state.
   */
  updateMeasurement: (
    successCallback?: () => void,
    include: string[] = [],
    { silent = false }: { silent?: boolean } = {},
  ): ThunkResult<Promise<boolean>> => {
    return async (dispatch: RootDispatchType, getState: RootGetState): Promise<boolean> => {
      const measurement = currentMeasurementHydrated(getState());
      if (!measurement) {
        addFlashAlert("danger", "No measurement id found");
        return false;
      }

      try {
        dispatch(MeasurementActions.fetchMeasurement(measurement.id));

        const response = await update(measurement, include);

        dispatch(MeasurementActions.receiveMeasurement(response.measurement));

        if (!silent) {
          dispatch(MeasurementActions.setMeasurementEditing(false));
          addFlashAlert(SUCCESS, "Measurement updated successfully");
        }
        if (successCallback) {
          successCallback();
        }
        return true;
      } catch (response) {
        handleErrors(response);
        dispatch(MeasurementActions.setMeasurementLoaded(measurement.id));
        return false;
      }
    };
  },
};
